import {
    forwardRef,
    PropsWithChildren,
    ReactElement,
    ReactNode,
    SyntheticEvent,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import {
    useFloating,
    autoUpdate,
    shift,
    offset,
    useFocus,
    useDismiss,
    useClick,
    useTransitionStatus,
    useInteractions,
    Placement,
    FloatingPortal,
} from '@floating-ui/react'
import clsx from 'clsx'

import styles from './Popup.module.scss'

interface Props extends PropsWithChildren {
    triggerComponent: ReactNode | ((isOpen: boolean) => ReactElement)
    trigger?: 'hover' | 'click' | 'manual'
    placement?: Placement
    offset?: number
    className?: string
    attachToTrigger?: boolean
    isShow?: boolean
    closeOnClick?: boolean
    focusDismiss?: boolean
}

export interface Ref {
    openPopup: () => void
    closePopup: () => void
}

export const Popup = forwardRef<Ref, Props>(
    (
        {
            triggerComponent,
            trigger = 'click',
            placement = 'top',
            offset: _offset = 8,
            attachToTrigger = false,
            isShow = true,
            className,
            children,
            closeOnClick = false,
            focusDismiss = true,
        },
        ref
    ) => {
        const rootRef = useRef<HTMLDivElement>(null)
        const [open, setOpen] = useState(false)
        const { x, y, refs, strategy, context } = useFloating({
            open,
            onOpenChange: (state) => {
                if (trigger !== 'manual') {
                    setOpen(state)
                }
            },
            placement,
            whileElementsMounted: autoUpdate,
            middleware: [offset(_offset), shift()],
        })

        const focus = useFocus(context, {
            enabled: focusDismiss, // Don't dismiss on non-visible focus changes
        })
        const dismiss = useDismiss(context)
        const click = useClick(context, {
            enabled: trigger === 'click',
        })
        const { isMounted, status } = useTransitionStatus(context)
        const { getReferenceProps, getFloatingProps } = useInteractions([focus, dismiss, click])

        useImperativeHandle(ref, () => ({ closePopup: () => setOpen(false), openPopup: () => setOpen(true) }))

        return (
            <div ref={rootRef} className={styles.root} data-popup data-popup-open={open}>
                <div
                    ref={refs.setReference}
                    {...getReferenceProps()}
                    onClick={(e: SyntheticEvent) => {
                        e.stopPropagation()
                        e.nativeEvent.stopImmediatePropagation()

                        if (getReferenceProps().onClick) {
                            ;(getReferenceProps().onClick as (event: SyntheticEvent) => void)(e)
                        }
                    }}
                >
                    {typeof triggerComponent === 'function' ? triggerComponent(open) : triggerComponent}
                </div>
                {isShow && isMounted && (
                    <FloatingPortal root={attachToTrigger ? rootRef.current : undefined}>
                        <div
                            ref={refs.setFloating}
                            style={{
                                position: strategy,
                                top: y ?? 0,
                                left: x ?? 0,
                            }}
                            data-status={status}
                            className={clsx(styles.popup, className)}
                            {...getFloatingProps()}
                            onClick={(e: SyntheticEvent) => {
                                e.stopPropagation()
                                e.nativeEvent.stopImmediatePropagation()
                                closeOnClick && setOpen(false)

                                if (getFloatingProps().onClick) {
                                    ;(getFloatingProps().onClick as (event: SyntheticEvent) => void)(e)
                                }
                            }}
                        >
                            {children}
                        </div>
                    </FloatingPortal>
                )}
            </div>
        )
    }
)
